/**
 * Дособрано ли задание: есть ли у него верный ответ.
 *
 * Зачем модуль. Компиляция задания без верного ответа отдаёт `key: null` и
 * успех, а не отказ (`compileExercise`, разбор там же): человек ещё печатает
 * условие, и отказ выбросил бы первый же введённый символ. Такое задание молча
 * не попадает в домашку (`apps/api/src/homework-exercises.ts`), и об этом надо
 * сказать ДО нажатия: форма раздачи иначе показывает «Задания (8)» над
 * восемью галочками и создаёт пять работ на пять заданий.
 *
 * ⚠️ **Ни одного импорта из-за барьера, и это условие задачи, а не стиль.**
 * `ExerciseSource` вместе с `compileExercise` живёт в `@edu/shared/exercises`, а
 * этот модуль реэкспортируется из `index.ts`, то есть утащил бы компиляцию в
 * бандл ученика вместе с ответами (5.3 CLAUDE.md). Поэтому источник читается
 * мягко, из `unknown`, ровно тем же приёмом, что и авторский вид задания на
 * слайде (`apps/web/src/features/plans/author-view.ts`).
 *
 * ⚠️ **Здесь второе написание правила, и разъезд с первым ловится тестом.**
 * Правда живёт в `compileExercise`, а сверка стоит в `exercise-draft.test.ts`:
 * таблица источников гоняется через обе функции, и ответы обязаны совпасть до
 * буквы. Без этой сверки «дособрано» на экране и `key: null` на сервере
 * разъехались бы молча, а стоит такой разъезд ровно того, ради чего модуль и
 * заведён: числа, которое врёт.
 *
 * ⚠️ Испорченная запись это «ничего не утверждаем», то есть `false`. Такой
 * источник до эталона всё равно не доживёт: его отвергнет схема
 * (`PlanExerciseEntry`), и сервер ответит `invalid_exercise_source`. Отказ
 * лучше пометки: пометка сказала бы «доделайте в редакторе» там, где доделывать
 * нечего.
 */

function asRecord(source: unknown): Record<string, unknown> | null {
  if (typeof source !== "object" || source === null || Array.isArray(source)) return null;
  return source as Record<string, unknown>;
}

/**
 * Ни один вариант не отмечен верным.
 *
 * ⚠️ Обратный случай (верных несколько при «ответ один») это НЕ «не дособрано»,
 * а отказ компиляции `multiple_conflict`. Здесь он даёт `false`, и это верно:
 * задание с таким источником обязано доехать до сервера и получить честный
 * отказ, а не тихо выпасть из домашки с пометкой «доделайте».
 */
function choiceUnfinished(record: Record<string, unknown>): boolean {
  if (!Array.isArray(record.options)) return false;
  return !record.options.some((raw) => asRecord(raw)?.correct === true);
}

/**
 * Ни одной принятой строки, кроме пробелов.
 *
 * Схема требует хотя бы одну строку в `accept`, и свежесозданное задание
 * удовлетворяет её ПУСТОЙ строкой: то есть «не дособрано» здесь выглядит как
 * заполненное поле, и увидеть его можно только так.
 */
function inputUnfinished(record: Record<string, unknown>): boolean {
  if (!Array.isArray(record.accept)) return false;
  return !record.accept.some(
    (raw) => typeof raw === "string" && raw.trim().length > 0,
  );
}

/**
 * Пропусков нет вовсе либо у пропуска нет ни одной непустой альтернативы.
 *
 * ⚠️ **Здесь ВТОРОЕ написание разбора шаблона**, и первое живёт в
 * `parseBlanksTemplate` за барьером. Копии две по той же причине, по которой
 * существует весь этот модуль: тот разбор возвращает принятые ответы, то есть
 * ему нельзя в бандл ученика ни одним импортом. Совпадение двух написаний
 * охраняет `exercise-draft.test.ts`, и таблица там перечисляет в том числе все
 * случаи экранирования: разъехавшись, копии дали бы «дособрано» на экране и
 * `key: null` на сервере.
 *
 * Разбор посимвольный и повторяет форму первого написания дословно, вплоть до
 * выхода `closable`: так их можно сверить глазами, а не только тестом. Отличие
 * ровно одно и оно по задаче: здесь не нужны ни текст, ни сами ответы, только
 * «сколько пропусков» и «у всех ли есть непустая альтернатива».
 */
const OPEN = "{{";
const CLOSE = "}}";
const ESCAPE = "\\";

/** `\{{` или `\}}` в этой позиции. Разбор в шапке `escapedPairAt` первой копии. */
function escapedPair(template: string, at: number): boolean {
  if (template[at] !== ESCAPE) return false;
  const pair = template.slice(at + 1, at + 1 + OPEN.length);
  return pair === OPEN || pair === CLOSE;
}

function closingAt(template: string, from: number): number {
  let at = from;
  while (at < template.length) {
    if (escapedPair(template, at)) {
      at += 1 + OPEN.length;
      continue;
    }
    if (template.startsWith(CLOSE, at)) return at;
    at += 1;
  }
  return -1;
}

/** Есть ли у пропуска хоть одна непустая альтернатива. Палку экранирует слеш. */
function anyAlternative(body: string): boolean {
  let current = "";
  let at = 0;
  while (at < body.length) {
    if (escapedPair(body, at)) {
      current += body.slice(at + 1, at + 1 + OPEN.length);
      at += 1 + OPEN.length;
      continue;
    }
    if (body[at] === ESCAPE && body[at + 1] === "|") {
      current += "|";
      at += 2;
      continue;
    }
    if (body[at] === "|") {
      if (current.trim().length > 0) return true;
      current = "";
      at += 1;
      continue;
    }
    current += body[at];
    at += 1;
  }
  return current.trim().length > 0;
}

/** Пропуски шаблона: сколько их и есть ли среди них пустой. */
function blanksOf(template: string): { total: number; empty: boolean } {
  let total = 0;
  let empty = false;
  let at = 0;
  let closable = true;
  while (at < template.length) {
    if (escapedPair(template, at)) {
      at += 1 + OPEN.length;
      continue;
    }
    if (closable && template.startsWith(OPEN, at)) {
      const close = closingAt(template, at + OPEN.length);
      if (close >= 0) {
        total += 1;
        if (!anyAlternative(template.slice(at + OPEN.length, close))) empty = true;
        at = close + CLOSE.length;
        continue;
      }
      closable = false;
    }
    at += 1;
  }
  return { total, empty };
}

function blanksUnfinished(record: Record<string, unknown>): boolean {
  const template = record.template;
  if (typeof template !== "string") return false;
  const blanks = blanksOf(template);
  return blanks.empty || blanks.total === 0;
}

/**
 * `true` означает «верного ответа у задания пока нет».
 *
 * ⚠️ У ввода числа такого состояния нет вовсе, и это не забытая ветка: схема
 * источника не допускает пустого `value`, поэтому компиляция собирает эталон
 * всегда. Забытый ответ там виден иначе, на самом задании: авторский вид
 * печатает под условием то число, которое сейчас стоит эталоном.
 *
 * ⚠️ Виды, которых компиляция ещё не умеет (`match`, `order`, `expr`), тоже
 * дают `false`: они не «не дособраны», а отвергаются отказом
 * `exercise_kind_unsupported`. Пометка на них означала бы «доделайте», а
 * доделывать там нечего.
 *
 * Виды без эталона (`text`, `graph`, `derivation`, `column`) дают `false` по
 * той же причине: верного ответа у них нет не потому, что его забыли.
 */
export function isUnfinishedSource(source: unknown): boolean {
  const record = asRecord(source);
  if (record === null) return false;
  if (record.kind === "choice") return choiceUnfinished(record);
  if (record.kind === "input") return inputUnfinished(record);
  if (record.kind === "blanks") return blanksUnfinished(record);
  // Перевод без единого предложения компилируется в `key: null`, как выбор без
  // пометок (1.2 docs/translate-exercise-design.md)
  if (record.kind === "translate") return Array.isArray(record.items) && record.items.length === 0;
  return false;
}

/**
 * Задание ждёт ответа преподавателя: признак `review: "teacher"` стоит, а
 * верного ответа пока нет (решение 22 `docs/scan-extraction-design.md`).
 *
 * ⚠️ **Свой предикат, а `isUnfinishedSource` не меняется.** Это то же
 * «ключ неполон», но с другим следствием: недособранное запирает урок и
 * домашку, а такое задание урок НЕ держит (ответ звучит в записи или стоит на
 * другой странице, и проверяет его человек). Сольёшь их в один, и либо урок из
 * скана с аудированием перестанет создаваться, либо забытая пометка у обычного
 * выбора пройдёт в урок молча.
 *
 * Признак без неполного ключа это `false`: ключ дописали, задание обычное,
 * хоть признак и не успели снять. Признак у вида, где его не бывает, тоже
 * `false`: схема его там не держит, и утверждать по нему нечего.
 *
 * ⚠️ Пропуски без единого пропуска с признаком тоже `false`. Неполон там не
 * ответ, а само задание: условие переписали без пропусков, и довод решения 22
 * («ответ звучит в записи, проверяет человек») к нему не относится. Ответь
 * предикат `true`, задание прошло бы в урок без строки на экране: панель гасит
 * пометку «Пропусков в задании нет» по этому же предикату, а сервер
 * (`lesson-exercises.ts`) по нему же не отказывает.
 */
export function awaitsTeacher(source: unknown): boolean {
  const record = asRecord(source);
  if (record === null || record.review !== "teacher") return false;
  if (record.kind !== "choice" && record.kind !== "blanks" && record.kind !== "input") return false;
  // Пропусков не осталось ни одного: ждать ответа не от чего, ученику отвечать
  // не на что. Это недоделка, а не задание «проверяете вы», и урок она держит
  if (record.kind === "blanks" && typeof record.template === "string" && blanksOf(record.template).total === 0) {
    return false;
  }
  return isUnfinishedSource(record);
}
